'use client';

import { API_ROUTES, HTTP_METHODS, useApiMutation, useApiQuery } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { notFound } from 'next/navigation';
import { createContext, type Dispatch, type ReactNode, type SetStateAction, useContext, useState } from 'react';
import { useParticipationDate } from '../../ParticipationInfo/hooks';
import type { DetailedTicket, LotteryName, Participation } from '../../types';
import { PurchaseDetailsPageLoading } from '../loading';

const debug = createDebug({ isEnabled: false });

type CancelSubscriptionResponse = {
  status: string;
};

type PurchaseDetailsContextValue = {
  ticket: DetailedTicket;
  lotteryName: LotteryName;
  selectedDate: string;
  activeParticipation: Participation;
  numberOfDraws: number;
  dyjPlayed: boolean;
  lastDrawDate: string;
  effectiveCancelReceiptAt?: string;
  hasRenewal: boolean;
  isCancelModalOpen: boolean;
  isCancelling: boolean;
  showOlderParticipationsInfo: boolean;
  setShowOlderParticipationsInfo: Dispatch<SetStateAction<boolean>>;
  handleDateSelect: (date: Date) => void;
  handleCancelConfirm: () => void;
  openCancelModal: () => void;
  closeCancelModal: () => void;
};

const PurchaseDetailsContext = createContext<PurchaseDetailsContextValue | null>(null);

type PurchaseDetailsProviderProps = {
  ticketId: string;
  children: ReactNode;
};

/**
 * Fetches the purchased ticket and owns all page state (selected draw, cancel modal, cancellation).
 * Wraps the purchase details page so the `PurchaseDetails*` components stay thin and can be composed freely,
 * e.g. per-locale layouts. Handles loading, not-found and incomplete-data states before rendering children.
 */
function PurchaseDetailsProvider({ ticketId, children }: PurchaseDetailsProviderProps) {
  const { data, isLoading, error } = useApiQuery<DetailedTicket>(API_ROUTES.LOTTERIES.PURCHASED_TICKET_DETAILS.replace(':ticketId', ticketId), { credentials: true });
  const { mutate: cancelSubscription, isPending: isCancelling } = useApiMutation<CancelSubscriptionResponse>(
    API_ROUTES.LOTTERIES.CANCEL_SUBSCRIPTION.replace(':ticketId', ticketId),
    HTTP_METHODS.POST
  );

  const [userSelectedDate, setUserSelectedDate] = useState<string>();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancellationDate, setCancellationDate] = useState<string | null>(null);
  const [showOlderParticipationsInfo, setShowOlderParticipationsInfo] = useState(false);

  const participationDates = useParticipationDate(
    data?.participationInfo.map(({ drawingId, drawingDate, participationState }: Participation) => ({ drawingId, drawingDate, participationState })) ?? []
  );
  const defaultSelectedDate = participationDates.find(participation => participation.isSelected)?.drawingDate;
  const selectedDate = userSelectedDate ?? defaultSelectedDate;
  const activeParticipation = data?.participationInfo.find((participation: Participation) => participation.drawingDate === selectedDate);

  if (isLoading) return <PurchaseDetailsPageLoading />;
  if (error || !data) return notFound();

  const { lotteryName, participationInfo, dyj, renewType, cancelReceiptAt } = data;

  if (!selectedDate || !activeParticipation?.drawingId || !lotteryName) return null;

  const handleDateSelect = (date: Date) => {
    const draw = participationDates.find(draw => draw.date.getTime() === date.getTime());
    if (draw) setUserSelectedDate(draw.drawingDate);
  };

  const handleCancelConfirm = () => {
    cancelSubscription(
      { ticketId },
      {
        onSuccess: () => {
          setCancellationDate(new Date().toISOString());
          setIsCancelModalOpen(false);
        },
        onError: error => {
          debug.error('Failed to cancel subscription:', error);
          setIsCancelModalOpen(false);
        },
      }
    );
  };

  const value: PurchaseDetailsContextValue = {
    ticket: data,
    lotteryName,
    selectedDate,
    activeParticipation,
    numberOfDraws: participationInfo.length,
    dyjPlayed: !!dyj?.includes(lotteryName),
    lastDrawDate: participationInfo.reduce((latest, participation) =>
      new Date(participation.drawingDate).getTime() > new Date(latest.drawingDate).getTime() ? participation : latest
    ).drawingDate,
    effectiveCancelReceiptAt: cancelReceiptAt ?? cancellationDate ?? undefined,
    hasRenewal: renewType === 'Hunt' || renewType === 'Subscription',
    isCancelModalOpen,
    isCancelling,
    showOlderParticipationsInfo,
    setShowOlderParticipationsInfo,
    handleDateSelect,
    handleCancelConfirm,
    openCancelModal: () => setIsCancelModalOpen(true),
    closeCancelModal: () => setIsCancelModalOpen(false),
  };

  return <PurchaseDetailsContext.Provider value={value}>{children}</PurchaseDetailsContext.Provider>;
}

/** Returns purchase details state; throws when used outside `PurchaseDetailsProvider`. */
function usePurchaseDetails() {
  const context = useContext(PurchaseDetailsContext);
  if (!context) throw new Error('usePurchaseDetails must be used within PurchaseDetailsProvider');

  return context;
}

export { PurchaseDetailsProvider, usePurchaseDetails };
